import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';

/**
 * Implemented by components that hold work the user could lose on logout
 * (report editors etc.). Register with {@link PendingWorkService} on init and
 * unregister on destroy; the auto-logout flow calls saveAll() before it revokes
 * the session so unsaved changes are persisted first.
 */
export interface PendingWorkSaver {
    /** True when there are unsaved changes right now. */
    hasPendingWork(): boolean;
    /** Persists the work. Must complete (or error) — it is awaited before logout. */
    savePendingWork(): Observable<unknown>;
}

@Injectable({ providedIn: 'root' })
export class PendingWorkService {

    /** Hard cap per saver so a hung request can never block logout forever. */
    private static readonly SAVE_TIMEOUT_MS = 15000;

    private _savers = new Set<PendingWorkSaver>();

    register(saver: PendingWorkSaver): void {
        this._savers.add(saver);
    }

    unregister(saver: PendingWorkSaver): void {
        this._savers.delete(saver);
    }

    /**
     * Saves every registered saver that reports pending work.
     * Never errors and always completes — logout must proceed regardless of
     * how the individual saves went.
     */
    saveAll(): Observable<boolean> {
        const jobs = Array.from(this._savers)
            .filter((saver) => {
                try {
                    return saver.hasPendingWork();
                } catch {
                    return false;
                }
            })
            .map((saver) => {
                try {
                    return saver.savePendingWork().pipe(
                        timeout(PendingWorkService.SAVE_TIMEOUT_MS),
                        catchError(() => of(null)),
                    );
                } catch {
                    return of(null);
                }
            });

        if (jobs.length === 0) {
            return of(true);
        }

        return forkJoin(jobs).pipe(
            map(() => true),
            catchError(() => of(true)),
        );
    }
}
